import { Anchor, Breadcrumbs as MantineBreadcrumbs, BreadcrumbsProps, Text, useMantineTheme } from '@mantine/core';
import { IconChevronRight, IconHome } from '@tabler/icons-react';
import { forwardRef, ReactNode } from 'react';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: ReactNode;
}

export interface ThemedBreadcrumbsProps extends Omit<BreadcrumbsProps, 'children'> {
  items: BreadcrumbItem[];
  gradientVariant?: GradientVariant;
  showHome?: boolean;
  homeHref?: string;
}

const Breadcrumbs = forwardRef<HTMLDivElement, ThemedBreadcrumbsProps>(
  ({ items, gradientVariant = 'primary', showHome = true, homeHref = '/', separator, ...rest }, ref) => {
    const theme = useMantineTheme();
    const gradient = theme.other.gradients[gradientVariant];
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const allItems = showHome
      ? [{ label: 'Home', href: homeHref, icon: <IconHome size={14} /> }, ...items]
      : items;

    return (
      <MantineBreadcrumbs
        ref={ref}
        separator={separator || <IconChevronRight size={14} />}
        {...rest}
      >
        {allItems.map((item, index) => {
          const isLast = index === allItems.length - 1;

          if (isLast || !item.href) {
            return (
              <Text
                key={index}
                size="sm"
                fw={isLast ? 600 : 400}
                style={
                  isLast
                    ? {
                        backgroundImage: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        ...accessibilityStyles,
                      }
                    : { display: 'flex', alignItems: 'center', gap: 4, ...accessibilityStyles }
                }
              >
                {item.icon}
                {item.label}
              </Text>
            );
          }

          return (
            <Anchor
              key={index}
              href={item.href}
              size="sm"
              c="dimmed"
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}
            >
              {item.icon}
              {item.label}
            </Anchor>
          );
        })}
      </MantineBreadcrumbs>
    );
  }
);

Breadcrumbs.displayName = 'Breadcrumbs';

export default Breadcrumbs;
